import type { VersaoResumoResponse } from '@triade/contracts';
import { ProfitBadge } from '@/components/profit-badge';
import { formatarData, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ROTULO_VERSAO } from './acoes';

export interface ListaVersoesProps {
  versoes: VersaoResumoResponse[];
  /** Versao exibida no detalhe (destacada). */
  numeroAtual: number;
  /** Meta e minimo do orcamento (cabecalho da API; a faixa de cada versao vem da propria versao). */
  meta: string;
  minimo: string;
  onVer: (n: number) => void;
}

/**
 * Aba Versoes: uma linha por versao (inclusive descartadas). Tudo vem de GET :id/versoes. `lucroValor` (R$) so aparece
 * quando a API o envia (custo.ver); quem nao tem a permissao nao recebe a chave e a coluna some.
 */
export function ListaVersoes({ versoes, numeroAtual, meta, minimo, onVer }: ListaVersoesProps) {
  const mostrarLucroValor = versoes.some((v) => v.lucroValor !== undefined);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] border-collapse text-small" aria-label="Versões do orçamento">
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th scope="col" className="px-3 py-2.5 font-medium">Versão</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Situação da versão</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Data</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Enviada por</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Total</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Lucro</th>
            {mostrarLucroValor && <th scope="col" className="px-3 py-2.5 text-right font-medium">Lucro (R$)</th>}
            <th scope="col" className="px-3 py-2.5 font-medium">Validade</th>
          </tr>
        </thead>
        <tbody>
          {versoes.map((v) => (
            <tr
              key={v.id}
              data-testid="linha-versao"
              data-versao={v.numero}
              data-status={v.status}
              onClick={() => onVer(v.numero)}
              className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', v.numero === numeroAtual && 'bg-line-soft')}
            >
              <td className="px-3 py-3">
                <button type="button" onClick={(e) => { e.stopPropagation(); onVer(v.numero); }} className="font-semibold text-ink hover:underline">
                  {v.numeroExibido}
                </button>
                {v.referencia && <span className="ml-2 text-caption text-ink-muted">referência</span>}
                {v.criadaDeNumero !== null && <div className="text-caption text-ink-muted">copiada da v{v.criadaDeNumero}</div>}
              </td>
              <td className="px-3 py-3">
                {ROTULO_VERSAO[v.status]}
                {v.migrada && <div className="text-caption text-ink-muted">anterior às versões</div>}
              </td>
              <td className="tabular px-3 py-3 text-ink-secondary">{formatarData(v.data)}</td>
              <td className="px-3 py-3 text-ink-secondary">
                {v.enviadaPorNome ?? <span className="text-ink-disabled">—</span>}
                {v.enviadaEm && <div className="text-caption text-ink-muted">{formatarData(v.enviadaEm)}</div>}
              </td>
              <td className="tabular px-3 py-3 text-right font-semibold">{formatarMoeda(v.total)}</td>
              <td className="px-3 py-3 text-right">
                {v.lucro === null ? (
                  <span className="text-ink-disabled">—</span>
                ) : (
                  <ProfitBadge lucro={v.lucro} faixa={v.faixa ?? undefined} meta={meta} minimo={minimo} compacto />
                )}
              </td>
              {mostrarLucroValor && <td className="tabular px-3 py-3 text-right">{v.lucroValor !== undefined ? formatarMoeda(v.lucroValor) : '—'}</td>}
              <td className="px-3 py-3">
                <span className="tabular text-ink-secondary">{v.validaAte ? `até ${formatarData(v.validaAte)}` : `${v.validadeDias} dias`}</span>
                {v.vencido && <span className="ml-2 rounded-full bg-warning-bg px-2 py-0.5 text-caption font-semibold text-warning-ink">Vencido</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
